import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { getBilling } from '@/api/billing'
import type { Order } from '@/types/order'
import { LiveTimer } from '@/components/orders/LiveTimer'
import { StatusBadge } from '@/components/orders/StatusBadge'
import { OrderKindBadge } from '@/components/orders/OrderKindBadge'
import { formatRub, isOverdue } from '@/utils/format'
import type { OrderStatus } from '@/types/enums'
import { isTerminalStatus } from '@/utils/status-transitions'
import { prefetchOrderDetail } from '@/utils/prefetch'

export function KanbanCard({ order }: { order: Order }) {
  const qc = useQueryClient()
  const { data: billing } = useQuery({
    queryKey: ['billing', order.id],
    queryFn: () => getBilling(order.id),
    staleTime: 30_000,
  })
  const overdue = isOverdue(
    order.status,
    order.created_at,
    order.status_updated_at,
  )
  const archived = isTerminalStatus(order.status as OrderStatus)
  const warmUp = () => {
    qc.setQueryData(['order', order.id], order)
    prefetchOrderDetail(qc, order.id)
  }
  return (
    <Link
      to={`/orders/${order.id}`}
      onMouseEnter={warmUp}
      onFocus={warmUp}
      className={clsx(
        'relative block rounded-[var(--stryzh-radius-lg)] bg-[color:var(--stryzh-color-surface)] p-[var(--stryzh-space-5)] transition hover:-translate-y-0.5',
        archived && 'opacity-[var(--stryzh-opacity-archived)]',
        overdue
          ? 'border-[length:var(--stryzh-border-width-emphasis)] border-[color:var(--stryzh-color-semantic-error)] bg-[color:var(--stryzh-color-order-card-timeout-bg)]'
          : 'stryzh-kanban-card',
      )}
    >
      <div className="absolute right-[var(--stryzh-space-5)] top-[var(--stryzh-space-5)] z-10">
        <OrderKindBadge kind={order.customer_kind} />
      </div>
      <div className="min-w-0 truncate pr-16 font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-base)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
        {order.brand} {order.model}
      </div>
      {/* Имя клиента под маркой-моделью: физлицо → individual_customer_name,
       *  B2B → customer_company.name. Когда заказчик обращается с вопросом,
       *  диспетчер сразу видит «по кому» заказ, не открывая карточку. */}
      {(() => {
        const customer =
          order.customer_kind === 'individual'
            ? order.individual_customer_name
            : (order.customer_company?.name ?? null)
        if (!customer) return null
        return (
          <div className="mt-[var(--stryzh-space-1)] truncate pr-16 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {customer}
          </div>
        )
      })()}

      <div className="mt-[var(--stryzh-space-3)] truncate font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
        {order.address}
      </div>
      {order.address_city ? (
        <div className="mt-[var(--stryzh-space-2)] truncate font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {order.address_city}
        </div>
      ) : null}

      {billing ? (
        <div className="mt-[var(--stryzh-space-3)] font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] font-medium tabular-nums text-[color:var(--stryzh-color-text-primary)]">
          {formatRub(billing.totals.total_customer)}
        </div>
      ) : null}
      <div className="mt-[var(--stryzh-space-5)] flex min-w-0 items-center justify-between gap-[var(--stryzh-space-3)]">
        <div className="min-w-0 shrink">
          <StatusBadge status={order.status as OrderStatus} />
        </div>
        <div className="shrink-0">
          <LiveTimer
            status={order.status as OrderStatus}
            createdAt={order.created_at}
            statusUpdatedAt={order.status_updated_at}
          />
        </div>
      </div>
    </Link>
  )
}
